// src/pages/common/reports/R0006/components/R0006SalesPerformanceTable.js

import React, { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { Box, Typography, Tooltip } from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

/**
 * Enhanced Sales Performance Table Component for R0006
 * Now supports both area-level and store-level views with drill-down capability
 */
export default function R0006SalesPerformanceTable({ 
  data, 
  dataReady,
  // New props for area/store support
  isAreasView = false,
  currentView = 'areas',
  onRowClick = null,
  selectedAreaData = null
}) {
  // Enhanced column definitions that work for both areas and stores
  const columns = useMemo(
    () => [
      {
        accessorKey: 'restaurant',
        header: isAreasView ? 'Area' : 'Restaurant',
        cell: ({ getValue, row }) => (
          <span style={{ fontWeight: '500' }}>
            {getValue()}
            {/* Show store count for areas view */}
            {isAreasView && row.original.storeCount && (
              <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 'normal' }}>
                {row.original.storeCount} stores
              </div>
            )}
          </span>
        ),
      },
      {
        accessorKey: 'projection',
        header: 'Projection',
        cell: ({ getValue }) => (
          <div style={{ textAlign: 'center' }}>
            ${Math.round(getValue() || 0).toLocaleString()}
          </div>
        ),
      },
      {
        accessorKey: 'actual',
        header: 'Actual',
        cell: ({ getValue }) => (
          <div style={{ fontWeight: '500', textAlign: 'center' }}>
            ${Math.round(getValue() || 0).toLocaleString()}
          </div>
        ),
      },
      {
        accessorKey: 'variance',
        header: 'Variance',
        cell: ({ row }) => {
          const variance = row.original.actual - row.original.projection;
          const percentage = row.original.projection > 0 
            ? ((variance / row.original.projection) * 100).toFixed(1)
            : '0.0';
          const isPositive = variance >= 0;
          
          return (
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontWeight: '500',
                  color: isPositive ? '#2e7d32' : '#d32f2f'
                }}
              >
                {isPositive ? '+' : ''}${Math.round(variance).toLocaleString()}
              </div>
              <div
                style={{
                  fontSize: '0.75rem',
                  color: isPositive ? '#2e7d32' : '#d32f2f'
                }}
              >
                {isPositive ? '+' : ''}{percentage}%
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'checkCount',
        header: 'Check Count',
        cell: ({ getValue, row }) => {
          const actualCheckCount = getValue() || 0;
          const projCheckCount = row.original.projCheckCount || 0;
          const variance = actualCheckCount - projCheckCount;
          const isPositive = variance >= 0;
          
          const tooltipTitle = projCheckCount > 0 
            ? `Projected: ${projCheckCount.toFixed(2)}`
            : '';
          
          return (
            <Tooltip title={tooltipTitle} arrow placement="top">
              <div style={{ cursor: 'help', textAlign: 'center' }}>
                <div style={{ fontWeight: '500' }}>
                  {Math.round(actualCheckCount).toLocaleString()}
                </div>
                {projCheckCount > 0 && (
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: isPositive ? '#2e7d32' : '#d32f2f'
                    }}
                  >
                    {isPositive ? '+' : ''}{Math.round(variance).toLocaleString()}
                  </div>
                )}
              </div>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'avgCheck',
        header: 'Avg Check',
        cell: ({ getValue, row }) => {
          const avgCheck = row.original.checkCount > 0
            ? (row.original.actual / row.original.checkCount)
            : 0;

          // IMPORTANT: Use the ADJUSTED projAvgCheck from the service
          // The service applies avgCheckHigh threshold adjustments
          // Fallback to calculated value if not provided (for backwards compatibility)
          const projAvgCheck = row.original.projAvgCheck !== undefined && row.original.projAvgCheck !== null
            ? row.original.projAvgCheck
            : (row.original.projCheckCount > 0
                ? (row.original.projection / row.original.projCheckCount)
                : 0);

          const variance = avgCheck - projAvgCheck;
          const isPositive = variance >= 0;

          const tooltipTitle = projAvgCheck > 0
            ? `Projected: $${projAvgCheck.toFixed(2)}`
            : '';

          return (
            <Tooltip title={tooltipTitle} arrow placement="top">
              <div style={{ cursor: 'help', textAlign: 'center' }}>
                <div style={{ fontWeight: '500' }}>
                  ${avgCheck.toFixed(2)}
                </div>
                {projAvgCheck > 0 && (
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: isPositive ? '#2e7d32' : '#d32f2f'
                    }}
                  >
                    {isPositive ? '+' : ''}${variance.toFixed(2)}
                  </div>
                )}
              </div>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'opportunity',
        header: 'Opportunity',
        cell: ({ getValue, row }) => {
          const opportunity = getValue();
          if (!opportunity || opportunity === 0) {
            return (
              <div style={{ color: '#757575', textAlign: 'center' }}>
                N/A
              </div>
            );
          }
          
          const percentage = row.original.actual > 0 
            ? ((opportunity / row.original.actual) * 100).toFixed(1)
            : '0.0';
          
          return (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: '500', color: '#ed6c02' }}>
                ${opportunity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#ed6c02' }}>
                +{percentage}%
              </div>
            </div>
          );
        },
      },
    ],
    [isAreasView]
  );

  // Enhanced table data preparation
  const tableData = useMemo(() => {
    if (!data?.restaurants) return [];

    return data.restaurants.map(restaurant => ({
      restaurant: restaurant.store,
      projection: restaurant.projSales || 0,
      actual: restaurant.actSales || 0,
      checkCount: restaurant.actCheckCount || 0,
      projCheckCount: restaurant.projCheckCount || 0,
      projAvgCheck: restaurant.projAvgCheck, // IMPORTANT: Include adjusted projAvgCheck from service
      opportunity: restaurant.opportunity || 0,
      // Include area data for drill-down
      areaId: restaurant.areaId,
      storeId: restaurant.storeId,
      storeCount: restaurant.storeCount, // For areas view
      // Debug fields
      _unadjustedProjAvgCheck: restaurant._unadjustedProjAvgCheck,
      _avgCheckAdjustment: restaurant._avgCheckAdjustment,
      _hasThresholdAssignment: restaurant._hasThresholdAssignment,
      _storeAdjustments: restaurant._storeAdjustments // For area aggregated rows
    }));
  }, [data]);

  // Enhanced totals calculation
  const tableDataWithTotals = useMemo(() => {
    if (tableData.length === 0) return [];

    const totals = tableData.reduce(
      (acc, row) => ({
        restaurant: data?.totalRowLabel || 'TOTALS',
        projection: acc.projection + (row.projection || 0),
        actual: acc.actual + (row.actual || 0),
        checkCount: acc.checkCount + (row.checkCount || 0),
        projCheckCount: acc.projCheckCount + (row.projCheckCount || 0),
        opportunity: acc.opportunity + (row.opportunity || 0),
        storeCount: isAreasView ? acc.storeCount + (row.storeCount || 0) : null,
      }),
      {
        restaurant: 'TOTALS',
        projection: 0,
        actual: 0,
        checkCount: 0,
        projCheckCount: 0,
        opportunity: 0,
        storeCount: 0,
      }
    );

    // Calculate adjusted projAvgCheck for totals row using simple average of store adjustments
    // Formula: (sum of adjustments) / count of stores with assignments
    let allStoreAdjustments = [];

    tableData.forEach(row => {
      // If this is an area aggregated row (from Area Summary view)
      if (row._storeAdjustments && Array.isArray(row._storeAdjustments)) {
        // Merge the store adjustments from this area into the total
        allStoreAdjustments = allStoreAdjustments.concat(row._storeAdjustments);
      }
      // If this is an individual store row
      else if (row._hasThresholdAssignment) {
        const adjustment = row._avgCheckAdjustment || 0;
        allStoreAdjustments.push(adjustment);
      }
    });

    const averageAdjustment = allStoreAdjustments.length > 0
      ? allStoreAdjustments.reduce((sum, adj) => sum + adj, 0) / allStoreAdjustments.length / 100 // Convert cents to dollars
      : 0;

    const baseTotalsProjAvgCheck = totals.projCheckCount > 0
      ? totals.projection / totals.projCheckCount
      : 0;

    totals.projAvgCheck = baseTotalsProjAvgCheck + averageAdjustment;

    return [...tableData, totals];
  }, [tableData, isAreasView, data?.totalRowLabel]);

  // Define tableStyles before using them
  const tableStyles = {
    container: {
      marginBottom: '0.75rem',
    },
    card: {
      backgroundColor: 'white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      borderRadius: '8px',
      padding: '16px',
      border: '1px solid #e0e0e0',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '12px',
    },
    icon: {
      marginRight: '8px',
      color: '#1976d2',
      fontSize: '20px',
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#333333',
      margin: 0,
    },
    table: {
      width: '100%',
      minWidth: '700px',
      borderCollapse: 'collapse',
      fontSize: '13px',
    },
    th: {
      backgroundColor: 'transparent',
      padding: '4px 8px',
      textAlign: 'left',
      fontWeight: '500',
      borderBottom: '1px solid #e0e0e0',
      cursor: 'pointer',
      userSelect: 'none',
      fontSize: '13px',
      color: '#666666',
    },
    td: {
      padding: '4px 8px',
      borderBottom: '1px solid #f0f0f0',
      fontSize: '13px',
    },
    totalRow: {
      backgroundColor: '#f8f9fa',
      borderTop: '2px solid #e0e0e0',
      fontWeight: 'bold',
    },
    clickableRow: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
    },
  };

  const table = useReactTable({
    data: tableDataWithTotals,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (!dataReady) {
    return null;
  }

  // Check if sales data is available
  if (!data?.restaurants || data.restaurants.length === 0) {
    return (
      <div style={tableStyles.container}>
        <div style={tableStyles.card}>
          <div style={tableStyles.header}>
            <AttachMoneyIcon sx={tableStyles.icon} />
            <h3 style={tableStyles.title}>Sales Performance</h3>
          </div>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="error" sx={{ mb: 2 }}>
              Sales Data Unavailable
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {data?.salesError || 'No sales data available for the selected date range'}
            </Typography>
          </Box>
        </div>
      </div>
    );
  }

  return (
    <div style={tableStyles.container}>
      {/* White Card Container */}
      <div style={tableStyles.card}>
        {/* Enhanced Table Header */}
        <div style={tableStyles.header}>
          <AttachMoneyIcon sx={tableStyles.icon} />
          <h3 style={tableStyles.title}>
            Sales Performance
            {currentView === 'all-stores' ? ' - All Stores' : isAreasView ? ' by Area' : ` - ${selectedAreaData?.areaName || 'Stores'}`}
          </h3>
        </div>

        {/* Table */}
        <table style={tableStyles.table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{
                      ...tableStyles.th,
                      textAlign: header.column.id === 'restaurant' ? 'left' : 'center'
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder ? null : (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: header.column.id === 'restaurant' ? 'flex-start' : 'center'
                      }}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: ' 🔼',
                          desc: ' 🔽',
                        }[header.column.getIsSorted()] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, index) => {
              const isTotal = row.original.restaurant === 'TOTALS';
              const isClickable = isAreasView && !isTotal && onRowClick && row.original.areaId;
              
              return (
                <tr
                  key={row.id}
                  style={{
                    ...(isTotal ? tableStyles.totalRow : {}),
                    cursor: isClickable ? 'pointer' : 'default'
                  }}
                  onClick={() => {
                    if (isClickable) {
                      onRowClick(row.original);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (!isTotal) {
                      e.currentTarget.style.backgroundColor = isClickable ? '#f5f5f5' : '#fafafa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isTotal) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                  title={isClickable ? `Click to view ${row.original.restaurant} stores` : ''}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        ...tableStyles.td,
                        fontWeight: isTotal ? 'bold' : 'normal',
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}